import React, { useEffect } from 'react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';
import cn from 'classnames';
import { Box, Grid } from '@material-ui/core';
import { useWindowSize, useMeasure } from 'react-use';
import { AppBar, Page, DataGrid, Column, ScreenLoader, EmptyState } from 'components';
import { isEmpty } from 'lodash';
import { ru } from 'locale';
import { anketsStore, debtorStore } from 'stores';
import { Report as ReportBg } from 'resources/images';
import { columns } from './DebtorScreen.mock';
import useStyles from './DebtorScreen.styles';

const TABLE_TOP_MARGIN = 16;
const size = 13;

const DebtorScreen = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { debtorId } = useParams();
  const { height: pageHeight } = useWindowSize();
  const [ref, { height: appBarHeight }] = useMeasure();
  const { fetchState, report, debtor, createTableData } = debtorStore;
  const hasReport = !isEmpty(report);

  useEffect(
    () =>
      autorun(() => {
        debtorStore.getReport(anketsStore.anketId, debtorId);
      }),
    [debtorId],
  );

  const handleBackClick = () => {
    history.goBack();
  };

  const cellRenderer = ({ cellData, rowIndex, columnIndex }) => {
    if (rowIndex === 1 || rowIndex === 2 || (rowIndex - 2) % size === 0) {
      return <span className={classes.total}>{cellData}</span>;
    }

    if (columnIndex === 0) {
      return <span className={classes.period}>{cellData}</span>;
    }

    return cellData;
  };

  const renderAnalytics = () => {
    const data = createTableData(columns);
    const dataCount = data.length;
    const columnCount = columns.length;
    const tableHeight = pageHeight - appBarHeight - TABLE_TOP_MARGIN;

    return (
      <DataGrid
        rowGetter={({ index }) => data[index]}
        dataRowCount={data.length}
        fixedColumnCount={1}
        height={tableHeight}
      >
        {columns.map(({ dataKey, ...other }) => {
          return (
            <Column
              key={dataKey}
              dataKey={dataKey}
              cellRenderer={({ cellData, rowIndex, columnIndex }) => {
                return cellRenderer({
                  rowCount: dataCount,
                  columnCount,
                  cellData,
                  rowIndex,
                  columnIndex,
                });
              }}
              {...other}
            />
          );
        })}
      </DataGrid>
    );
  };

  const renderFinanceReport = () => {
    if (!hasReport) {
      return (
        <EmptyState
          image={{ source: ReportBg, alt: ru.listOfBeneficiaries }}
          title={ru.emptyDebtorAnalytics}
        />
      );
    }

    return renderAnalytics();
  };

  const renderSubtitle = () => {
    const salesOrganization = debtor?.salesOrganization || '';
    const debtorCode = debtor?.debtorCode ? `(${debtor.debtorCode})` : '';

    return `${salesOrganization} ${debtorCode}`;
  };

  return (
    <Page fullScreen style={{ minHeight: '100rvh' }}>
      <Grid container direction="column">
        <AppBar
          ref={ref}
          title={debtor?.debtorName || ''}
          subtitle={renderSubtitle()}
          onNavButtonClick={handleBackClick}
        />

        {fetchState === 'pending' && <ScreenLoader />}

        <Grid
          container
          item
          direction="column"
          justify={hasReport ? 'flex-start' : 'center'}
          className={cn({
            [classes.content]: !hasReport,
          })}
        >
          <Box my={2}>{fetchState === 'fulfilled' && renderFinanceReport()}</Box>
        </Grid>
      </Grid>
    </Page>
  );
});

export default DebtorScreen;
